import React from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment } from '@mui/material';

/**
 * NumberInput - Controlled numeric input with validation
 *
 * Features:
 * - Min/max validation
 * - Custom step size
 * - Unit display (e.g., "kW", "%")
 * - Precision formatting on blur
 * - Empty and negative sign handling
 *
 * @example
 * ```jsx
 * <NumberInput
 *   label="Max Power"
 *   value={maxPower}
 *   onChange={(val) => setMaxPower(val)}
 *   min={0}
 *   max={1000}
 *   step={0.1}
 *   unit="kW"
 *   precision={1}
 * />
 * ```
 */
const NumberInput = ({
    label,
    value,
    onChange,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    step = 1,
    unit = '',
    helperText = '',
    error = false,
    disabled = false,
    precision = null,
    fullWidth = true,
    ...otherProps
}) => {
    const [localValue, setLocalValue] = React.useState(value !== null && value !== undefined ? String(value) : '');

    React.useEffect(() => {
        setLocalValue(value !== null && value !== undefined ? String(value) : '');
    }, [value]);

    const handleChange = (e) => {
        const rawValue = e.target.value;

        // Allow empty string
        if (rawValue === '') {
            setLocalValue('');
            onChange(null);
            return;
        }

        // Allow negative sign
        if (rawValue === '-') {
            setLocalValue('-');
            return;
        }

        // Parse as number
        const numValue = parseFloat(rawValue);

        // Validate numeric
        if (isNaN(numValue)) {
            return;
        }

        // Update local state (allow intermediate values during typing)
        setLocalValue(rawValue);

        // Clamp to min/max
        let clampedValue = numValue;
        if (clampedValue < min) {
            clampedValue = min;
        }
        if (clampedValue > max) {
            clampedValue = max;
        }

        // Notify parent
        onChange(clampedValue);
    };

    const handleBlur = () => {
        // Format on blur
        if (localValue === '' || localValue === '-') {
            setLocalValue('');
            onChange(null);
            return;
        }

        const numValue = parseFloat(localValue);
        if (isNaN(numValue)) {
            setLocalValue('');
            onChange(null);
            return;
        }

        // Clamp and format
        let clampedValue = numValue;
        if (clampedValue < min) {
            clampedValue = min;
        }
        if (clampedValue > max) {
            clampedValue = max;
        }

        // Apply precision formatting
        const formatted = precision !== null
            ? clampedValue.toFixed(precision)
            : String(clampedValue);

        setLocalValue(formatted);
        onChange(clampedValue);
    };

    return (
        <TextField
            label={label}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={(e) => e.target.select()}
            type="number"
            inputProps={{
                min,
                max,
                step,
            }}
            InputProps={unit ? {
                endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
            } : undefined}
            helperText={helperText}
            error={error}
            disabled={disabled}
            fullWidth={fullWidth}
            {...otherProps}
        />
    );
};

NumberInput.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    unit: PropTypes.string,
    helperText: PropTypes.string,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    precision: PropTypes.number,
    fullWidth: PropTypes.bool,
};

export default NumberInput;
